import { Listbox, ListboxOption, ListboxOptions, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { omit } from 'lodash'
import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'

import Banner from '@/components/TailwindUI/Banner'
import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import Skeleton from '@/components/TailwindUI/Skeleton'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { App, Feature, TriggerTypes } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { featureQueryOptions, getFetchAppQueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import cn from '@/utilities/cn'

type Props = {
  readOnly?: boolean
}

const none: Feature = {
  id: '123',
  name: 'None'
}

function findDefaultUI(features: Feature[] | undefined) {
  return features?.find((feature: Feature) => feature.isDefault) || none
}

function filterAppUIs(features: Feature[] | undefined) {
  return features?.filter((feature: Feature) => feature.type === TriggerTypes.microappView) || []
}

export default function DefaultUI({ readOnly = true }: Props) {
  const { spaceId, appId } = useParams<{ spaceId: string; appId: string }>()

  const fetchAppQuery = getFetchAppQueryOptions({ spaceId, appId })

  const queryClient = useQueryClient()
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]

  const initialDefaultFeature = queryClient
    .getQueryData<App>(fetchAppQuery.queryKey)
    ?.features?.find(feature => feature.isDefault)

  const featureMutation = useMutation({})
  const featuresQuery = useQuery<App>({
    ...fetchAppQuery,

    onSuccess: data => {
      const defaultUI = findDefaultUI(data?.features)
      if (defaultUI) {
        setSelected(defaultUI)
      }
    }
  })

  const listItems = [none, ...filterAppUIs(featuresQuery.data?.features)]

  const isEmpty = listItems?.length <= 1
  const isLoading = featuresQuery.isLoading
  const isLocked = featureMutation.isLoading

  const [selected, setSelected] = useState<Feature | undefined>(initialDefaultFeature || none)

  async function handleSelect(feature: Feature) {
    setSelected(feature)

    let featureToUpdate = { ...feature, isDefault: true }

    if (feature.name === 'None') {
      const existingDefaultUI = findDefaultUI(listItems)
      if (!existingDefaultUI) return
      featureToUpdate = { ...existingDefaultUI, isDefault: false }
    }

    await featureMutation.mutateAsync(
      // @ts-expect-error
      [
        featureQueryOptions({
          spaceId,
          appId,
          featureId: featureToUpdate.id
        }).queryKey[0],
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(
            omit(featureToUpdate, [
              'id',
              'workflow',
              'createdBy',
              'modifiedBy',
              'createdDate',
              'modifiedDate',
              'audience'
            ])
          )
        }
      ],
      {
        onSuccess: async () => {
          await featuresQuery.refetch()
        }
      }
    )
  }

  return isEmpty ? null : (
    <div className="mt-2 w-full bg-white p-4">
      <div className="h-10 sm:flex sm:items-center">
        <h1 className="text-lg font-semibold leading-6 text-gray-900">Default UI</h1>
      </div>
      <hr className="mt-2" />
      <div className="flow-root">
        <ul className="divide-y divide-gray-100">
          {isLoading ? (
            <li data-testid="loader" className="pointer-events-none flex items-center justify-between p-4">
              <Skeleton className="h-4 w-full pr-8" />
            </li>
          ) : listItems ? (
            <Listbox by="id" disabled={readOnly || isLocked} value={selected} onChange={handleSelect}>
              {({ open }) => (
                <div className="relative mt-4">
                  <Listbox.Button
                    className={cn(
                      'relative h-10 w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6',
                      {
                        'bg-gray-50': readOnly || isLocked
                      }
                    )}
                  >
                    <span className="block truncate">{selected?.name}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      {isLocked ? (
                        <div className="flex items-center justify-center">
                          <LoadingSpinner />
                        </div>
                      ) : (
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      )}
                    </span>
                  </Listbox.Button>

                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {listItems.map(feature => (
                        <ListboxOption
                          key={feature.id}
                          className={({ focus }) =>
                            cn(
                              focus ? 'bg-blue-600 text-white' : 'text-gray-900',
                              'relative cursor-default select-none py-2 pl-3 pr-9'
                            )
                          }
                          value={feature}
                        >
                          {({ selected, focus }) => (
                            <>
                              <span className={cn(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                {feature.name}
                              </span>

                              {selected && (
                                <span
                                  className={cn(
                                    focus ? 'text-white' : 'text-blue-600',
                                    'absolute inset-y-0 right-0 flex items-center pr-4'
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                  {selected?.id === none.id && (
                    <Banner variant="warning" className="mt-2 border-l-4 border-yellow-400">
                      No default UI is selected, {agentsFlagOn ? 'capabilities' : 'features'} with an{' '}
                      {agentsFlagOn ? 'Agent' : 'App'} UI trigger will not be visible to the{' '}
                      {agentsFlagOn ? 'agent' : 'app'} audience.
                    </Banner>
                  )}
                </div>
              )}
            </Listbox>
          ) : null}
        </ul>
      </div>
    </div>
  )
}
